import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { supabase } from "../supabaseClient";

const ADMIN_TOKEN_KEY = "outfall_admin_session_token";
const ADMIN_TOKEN_META_KEY = "outfall_admin_session_meta";
const ADMIN_BANS_CACHE_KEY = "outfall_admin_bans_cache";
const ADMIN_BANS_CACHE_MAX_AGE = 1000 * 60 * 5;

function createLocalToken() {
  if (crypto?.randomUUID) return crypto.randomUUID();

  return `admin_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function getStoredAdminToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredAdminToken(user) {
  try {
    const existingToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    const token = existingToken || createLocalToken();

    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    localStorage.setItem(
      ADMIN_TOKEN_META_KEY,
      JSON.stringify({
        discordId: user?.discordId || null,
        username: user?.username || null,
        createdAt: Date.now(),
      }),
    );

    return token;
  } catch {
    return null;
  }
}

function clearAdminLocalSession() {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_META_KEY);
    localStorage.removeItem(ADMIN_BANS_CACHE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

function getCachedBans() {
  try {
    const raw = localStorage.getItem(ADMIN_BANS_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed?.createdAt || !Array.isArray(parsed?.bans)) {
      return null;
    }

    if (Date.now() - parsed.createdAt > ADMIN_BANS_CACHE_MAX_AGE) {
      localStorage.removeItem(ADMIN_BANS_CACHE_KEY);
      return null;
    }

    return parsed.bans;
  } catch {
    return null;
  }
}

function setCachedBans(bans) {
  try {
    localStorage.setItem(
      ADMIN_BANS_CACHE_KEY,
      JSON.stringify({
        createdAt: Date.now(),
        bans,
      }),
    );
  } catch {
    // Ignore localStorage errors
  }
}

function getCachedUser() {
  try {
    return JSON.parse(sessionStorage.getItem("admin_user_cache"));
  } catch {
    return null;
  }
}

function getCachedRole() {
  try {
    return JSON.parse(sessionStorage.getItem("admin_role_cache"));
  } catch {
    return null;
  }
}

async function callDatastore(payload) {
  const adminToken = getStoredAdminToken();

  const res = await fetch("/api/datastore-proxy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-local-admin-session": adminToken || "",
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || data.message || "Datastore request failed.");
  }

  return data;
}

function formatRemainingTime(unbanTime) {
  const now = Math.floor(Date.now() / 1000);
  const remaining = Math.max(0, Number(unbanTime || 0) - now);

  if (remaining <= 0) return null;

  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (days > 0) return `${days}d ${hh}:${mm}:${ss}`;

  const time = `${hh}:${mm}:${ss}`;
  if (time === "00:00:00") return null;

  return time;
}

function formatUnixDate(unix) {
  if (!unix) return null;

  return new Date(Number(unix) * 1000).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export default function Admin() {
  const location = useLocation();

  const cachedUser = getCachedUser();
  const cachedRole = getCachedRole();

  const [loading, setLoading] = useState(!cachedUser);
  const [roleLoading, setRoleLoading] = useState(!cachedRole);
  const [user, setUser] = useState(cachedUser);
  const [allowed, setAllowed] = useState(Boolean(cachedRole?.allowed));
  const [error, setError] = useState("");

  useEffect(() => {
    checkSession({ silent: Boolean(cachedUser) });

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        sessionStorage.removeItem("admin_user_cache");
        sessionStorage.removeItem("admin_role_cache");
        clearAdminLocalSession();

        setUser(null);
        setAllowed(false);
        setLoading(false);
        setRoleLoading(false);
      }

      if (event === "SIGNED_IN") {
        checkSession({ silent: false, forceRoleCheck: true });
      }
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  async function checkSession({ silent = false, forceRoleCheck = false } = {}) {
    if (!silent) {
      setLoading(true);
      setRoleLoading(true);
    }

    setError("");

    const {
      data: { session },
    } = await supabase.auth.getSession();

    const currentUser = session?.user;

    if (!currentUser) {
      sessionStorage.removeItem("admin_user_cache");
      sessionStorage.removeItem("admin_role_cache");
      clearAdminLocalSession();

      setUser(null);
      setAllowed(false);
      setLoading(false);
      setRoleLoading(false);
      return;
    }

    const discordId =
      currentUser.user_metadata?.provider_id ||
      currentUser.user_metadata?.sub ||
      currentUser.identities?.[0]?.identity_data?.sub;

    const formattedUser = {
      ...currentUser,
      discordId,
      username:
        currentUser.user_metadata?.full_name ||
        currentUser.user_metadata?.name ||
        currentUser.user_metadata?.custom_claims?.global_name ||
        "Discord User",
      avatar: currentUser.user_metadata?.avatar_url,
    };

    setStoredAdminToken(formattedUser);
    sessionStorage.setItem("admin_user_cache", JSON.stringify(formattedUser));

    setUser(formattedUser);
    setLoading(false);

    const cachedRoleRaw = sessionStorage.getItem("admin_role_cache");

    if (cachedRoleRaw && !forceRoleCheck) {
      try {
        const parsed = JSON.parse(cachedRoleRaw);

        if (parsed.discordId === discordId) {
          setAllowed(Boolean(parsed.allowed));
          setRoleLoading(false);
          return;
        }
      } catch {
        sessionStorage.removeItem("admin_role_cache");
      }
    }

    if (!silent) setRoleLoading(true);

    try {
      const roleRes = await fetch("/api/check-staff-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-local-admin-session": getStoredAdminToken() || "",
        },
        body: JSON.stringify({
          discord_id: discordId,
          secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
        }),
      });

      const roleData = await roleRes.json();
      const hasRole = Boolean(roleData.hasRole);

      setAllowed(hasRole);

      sessionStorage.setItem(
        "admin_role_cache",
        JSON.stringify({
          discordId,
          allowed: hasRole,
          checkedAt: Date.now(),
        }),
      );
    } catch {
      setAllowed(false);
      setError("Could not verify your Discord staff role.");
    }

    setRoleLoading(false);
  }

  async function handleLogout() {
    sessionStorage.removeItem("admin_user_cache");
    sessionStorage.removeItem("admin_role_cache");
    clearAdminLocalSession();

    await supabase.auth.signOut();

    setUser(null);
    setAllowed(false);
    setLoading(false);
    setRoleLoading(false);
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />

        <section className="flex min-h-screen items-center justify-center px-6">
          <p className="font-black uppercase tracking-[0.18em] text-white/40">
            Checking session...
          </p>
        </section>
      </main>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(
          location.pathname + location.search,
        )}`}
        replace
      />
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      <Header />

      <section className="relative px-6 pt-36 pb-20">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_left,rgba(255,217,120,0.18),transparent_35%),radial-gradient(circle_at_top_right,rgba(255,107,0,0.16),transparent_35%)]" />

        <div className="relative mx-auto max-w-7xl">
          <header className="mb-10 rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/80 p-8 shadow-[0_0_35px_rgba(255,143,107,0.12)] backdrop-blur">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="mb-3 font-black uppercase tracking-[0.25em] text-[#ffb56f]">
                  Roblox Datastore
                </p>

                <h1 className="outfall-heading text-[clamp(44px,7vw,92px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
                  Outfall Ban Panel
                </h1>

                <p className="mt-5 max-w-3xl text-lg font-semibold leading-relaxed text-white/60">
                  Our secure moderation panel to ensure every ban is logged, and
                  able to be revoked and amended with ease.
                </p>
              </div>

              <div className="inline-flex w-fit items-center gap-3 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-5 py-3 font-black uppercase text-emerald-300 shadow-[0_0_18px_rgba(16,185,129,0.15)]">
                <span className="h-3 w-3 rounded-full bg-emerald-400 shadow-[0_0_14px_#34d399]" />
                Discord Role Protected
              </div>
            </div>
          </header>

          {roleLoading && (
            <section className="mx-auto max-w-2xl rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/90 p-8 text-center shadow-[0_0_35px_rgba(255,217,120,0.08)]">
              <p className="font-black uppercase tracking-[0.18em] text-white/40">
                Checking Discord staff role...
              </p>
            </section>
          )}

          {!roleLoading && !allowed && (
            <AccessDenied user={user} error={error} onLogout={handleLogout} />
          )}

          {!roleLoading && allowed && (
            <>
              <SignedInBar user={user} onLogout={handleLogout} />
              <AdminPanel />
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function AccessDenied({ user, error, onLogout }) {
  return (
    <section className="mx-auto max-w-2xl rounded-[2rem] border border-red-400/25 bg-red-950/20 p-8 shadow-[0_0_35px_rgba(248,113,113,0.08)]">
      <h2 className="text-3xl font-black text-red-300">Access Denied</h2>

      <p className="mt-3 font-semibold text-white/55">
        Your Discord account is signed in, but you do not have one of the
        required admin roles.
      </p>

      {error && (
        <p className="mt-5 rounded-xl bg-red-500/20 px-4 py-3 text-sm font-bold text-red-300">
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
        {user.avatar && (
          <img
            src={user.avatar}
            alt=""
            className="h-12 w-12 rounded-full border border-white/10"
          />
        )}

        <div>
          <p className="font-black text-white">{user.username}</p>
          <p className="font-mono text-sm text-white/35">{user.discordId}</p>
        </div>
      </div>

      <button
        onClick={onLogout}
        type="button"
        className="mt-6 rounded-2xl border border-red-300/30 bg-red-400/10 px-6 py-3 font-black uppercase text-red-300 transition hover:bg-red-400/20"
      >
        Sign Out
      </button>
    </section>
  );
}

function SignedInBar({ user, onLogout }) {
  return (
    <div className="mb-6 flex flex-col gap-4 rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/80 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        {user.avatar && (
          <img
            src={user.avatar}
            alt=""
            className="h-12 w-12 rounded-full border border-[#ffd978]/30"
          />
        )}

        <div>
          <p className="font-black text-[#ffd978]">
            Signed in as {user.username}
          </p>
          <p className="font-mono text-sm text-white/35">{user.discordId}</p>
        </div>
      </div>

      <button
        onClick={onLogout}
        type="button"
        className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-3 font-black uppercase text-white/60 transition hover:border-red-400/30 hover:bg-red-400/10 hover:text-red-300"
      >
        Sign Out
      </button>
    </div>
  );
}

function AdminPanel() {
  const cachedBans = getCachedBans();

  const [bans, setBans] = useState(cachedBans || []);
  const [selectedBan, setSelectedBan] = useState(null);
  const [loadingBans, setLoadingBans] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(
    cachedBans ? "Bans restored from local session." : "",
  );
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    userId: "",
    reason: "",
    permanent: false,
    days: "",
    hours: "",
    minutes: "",
  });

  function updateForm(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function resetForm() {
    setSelectedBan(null);
    setForm({
      userId: "",
      reason: "",
      permanent: false,
      days: "",
      hours: "",
      minutes: "",
    });
  }

  async function loadBans({ force = false } = {}) {
    if (!force) {
      const cached = getCachedBans();

      if (cached) {
        setBans(cached);
        setMessage("Bans restored from local session.");
        setError("");
        return;
      }
    }

    setLoadingBans(true);
    setMessage("");
    setError("");

    try {
      const data = await callDatastore({
        action: "listBans",
        datastoreName: "BanStore",
      });

      const cleanBans = Array.isArray(data.bans)
        ? data.bans.filter((entry) => Boolean(entry?.value))
        : [];

      setBans(cleanBans);
      setCachedBans(cleanBans);

      const deletedText =
        data.deletedExpired > 0
          ? ` Removed ${data.deletedExpired} expired ban(s).`
          : "";

      setMessage(`Bans loaded successfully.${deletedText}`);
    } catch (err) {
      setError(err.message || "Could not load bans.");
    }

    setLoadingBans(false);
  }

  async function saveBan() {
    setSaving(true);
    setMessage("");
    setError("");

    const userId = String(form.userId || "").trim();
    const reason = String(form.reason || "").trim();

    if (!userId) {
      setError("UserId is required.");
      setSaving(false);
      return;
    }

    if (!reason) {
      setError("Reason is required.");
      setSaving(false);
      return;
    }

    const days = Number(form.days || 0);
    const hours = Number(form.hours || 0);
    const minutes = Number(form.minutes || 0);
    const durationSeconds = days * 86400 + hours * 3600 + minutes * 60;

    if (!form.permanent && durationSeconds <= 0) {
      setError("Temporary bans need at least 1 minute.");
      setSaving(false);
      return;
    }

    try {
      await callDatastore({
        action: selectedBan ? "editBan" : "ban",
        datastoreName: "BanStore",
        entryKey: userId,
        reason,
        permanent: Boolean(form.permanent),
        days: form.days,
        hours: form.hours,
        minutes: form.minutes,
      });

      setMessage(
        selectedBan ? "Ban updated successfully." : "Ban created successfully.",
      );

      resetForm();
      localStorage.removeItem(ADMIN_BANS_CACHE_KEY);
      await loadBans({ force: true });
    } catch (err) {
      setError(err.message || "Could not save ban.");
    }

    setSaving(false);
  }

  async function deleteBan(userId) {
    const confirmDelete = window.confirm(
      `Are you sure you want to revoke/delete the ban for UserId ${userId}?`,
    );

    if (!confirmDelete) return;

    setMessage("");
    setError("");

    try {
      await callDatastore({
        action: "revokeBan",
        datastoreName: "BanStore",
        entryKey: String(userId),
      });

      setMessage("Ban revoked successfully.");

      localStorage.removeItem(ADMIN_BANS_CACHE_KEY);
      await loadBans({ force: true });

      if (String(selectedBan?.entryKey) === String(userId)) {
        resetForm();
      }
    } catch (err) {
      setError(err.message || "Could not revoke ban.");
    }
  }

  function editBan(entry) {
    const entryKey = entry.entryKey || entry.key || entry.userId || entry.id;
    const value = entry.value || {};

    const now = Math.floor(Date.now() / 1000);
    const unbanTime = Number(value.UnbanTime || 0);
    const remaining = Math.max(0, unbanTime - now);

    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);

    setSelectedBan({
      entryKey,
      value,
    });

    setForm({
      userId: String(entryKey || ""),
      reason: value.Reason || "",
      permanent: Boolean(value.Permanent),
      days: days > 0 ? String(days) : "",
      hours: hours > 0 ? String(hours) : "",
      minutes: minutes > 0 ? String(minutes) : "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    loadBans();
  }, []);

  return (
    <section className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
      <section className="rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/90 p-7 shadow-[0_0_35px_rgba(255,217,120,0.08)]">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-3xl font-black text-[#ffd978]">Current Bans</h2>
            <p className="mt-1 font-semibold text-white/45">
              View active ban entries stored in BanStore.
            </p>
          </div>

          <button
            onClick={() => loadBans({ force: true })}
            disabled={loadingBans}
            type="button"
            className="rounded-2xl border border-[#ffd978]/25 bg-[#ffd978]/10 px-5 py-3 font-black uppercase text-[#ffd978] transition hover:bg-[#ffd978]/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loadingBans ? "Loading..." : "Refresh Bans"}
          </button>
        </div>

        {message && (
          <p className="mb-5 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm font-black text-emerald-300">
            {message}
          </p>
        )}

        {error && (
          <p className="mb-5 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm font-black text-red-300">
            {error}
          </p>
        )}

        {bans.length <= 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/[0.03] p-10 text-center">
            <p className="font-black uppercase tracking-[0.18em] text-white/30">
              No bans loaded.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {bans.map((entry, index) => {
              const key =
                entry.entryKey ||
                entry.key ||
                entry.userId ||
                entry.id ||
                index;

              const value = entry.value || {};
              const isPermanent = value.Permanent === true;
              const unbanTime = Number(value.UnbanTime || 0);
              const remainingText = formatRemainingTime(unbanTime);
              const unbanDateText = formatUnixDate(unbanTime);

              return (
                <div
                  key={key}
                  className="rounded-3xl border border-white/10 bg-white/[0.04] p-5"
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-sm text-white/35">
                        UserId: {key}
                      </p>

                      <h3 className="mt-1 text-xl font-black text-white">
                        {value.Reason || "No reason provided"}
                      </h3>

                      {isPermanent && (
                        <p className="mt-2 font-black uppercase text-red-300">
                          Permanent Ban
                        </p>
                      )}

                      {!isPermanent && remainingText && (
                        <p className="mt-2 font-black text-[#ffd978]">
                          Time Remaining: {remainingText}
                        </p>
                      )}

                      {!isPermanent && unbanDateText && remainingText && (
                        <p className="mt-1 font-semibold text-white/40">
                          Unbans: {unbanDateText}
                        </p>
                      )}

                      {!isPermanent && !remainingText && (
                        <p className="mt-2 font-semibold text-white/35">
                          No active time set
                        </p>
                      )}

                      {value.UpdatedAt && (
                        <p className="mt-1 font-mono text-xs text-white/25">
                          Updated:{" "}
                          {formatUnixDate(value.UpdatedAt) || value.UpdatedAt}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => editBan(entry)}
                        type="button"
                        className="rounded-2xl border border-[#ffd978]/25 bg-[#ffd978]/10 px-5 py-3 font-black uppercase text-[#ffd978] transition hover:bg-[#ffd978]/20"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => deleteBan(key)}
                        type="button"
                        className="rounded-2xl border border-red-400/25 bg-red-400/10 px-5 py-3 font-black uppercase text-red-300 transition hover:bg-red-400/20"
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="rounded-[2rem] border border-[#ffd978]/20 bg-[#0b0b0b]/90 p-7 shadow-[0_0_35px_rgba(255,217,120,0.08)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black text-[#ffd978]">
              {selectedBan ? "Edit Ban" : "Create Ban"}
            </h2>

            <p className="mt-1 font-semibold text-white/45">
              Use a Roblox UserId as the datastore entry key.
            </p>
          </div>

          {selectedBan && (
            <button
              onClick={resetForm}
              type="button"
              className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-black uppercase text-white/50 transition hover:text-white"
            >
              New
            </button>
          )}
        </div>

        <div className="mt-7 space-y-5">
          <Field
            label="UserId"
            placeholder="9678448283"
            value={form.userId}
            onChange={(value) => updateForm("userId", value)}
            disabled={Boolean(selectedBan)}
          />

          <Field
            label="Reason"
            placeholder="Exploiting"
            value={form.reason}
            onChange={(value) => updateForm("reason", value)}
          />

          <label className="group flex cursor-pointer items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-[#ffd978]/30 hover:bg-white/[0.06]">
            <input
              checked={form.permanent}
              onChange={(e) => updateForm("permanent", e.target.checked)}
              type="checkbox"
              className="sr-only"
            />

            <span className="relative flex h-7 w-12 shrink-0 items-center rounded-full border border-white/10 bg-white/10 transition group-has-[:checked]:border-[#ffd978]/60 group-has-[:checked]:bg-gradient-to-r group-has-[:checked]:from-[#ffd978] group-has-[:checked]:to-[#ff6b00]">
              <span className="absolute left-1 h-5 w-5 rounded-full bg-white shadow-lg transition group-has-[:checked]:translate-x-5" />
            </span>

            <span>
              <span className="block font-black text-white">Permanent ban</span>
              <small className="text-white/40">
                Overrides days, hours, and minutes.
              </small>
            </span>
          </label>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="Days"
              type="number"
              placeholder="12"
              value={form.days}
              onChange={(value) => updateForm("days", value)}
              disabled={form.permanent}
            />

            <Field
              label="Hours"
              type="number"
              placeholder="0"
              value={form.hours}
              onChange={(value) => updateForm("hours", value)}
              disabled={form.permanent}
            />

            <Field
              label="Minutes"
              type="number"
              placeholder="0"
              value={form.minutes}
              onChange={(value) => updateForm("minutes", value)}
              disabled={form.permanent}
            />
          </div>

          <button
            onClick={saveBan}
            disabled={saving}
            type="button"
            className="min-h-[56px] w-full rounded-2xl bg-gradient-to-r from-[#ffd978] via-[#ffb56f] to-[#ff6b00] px-7 font-black uppercase text-black shadow-[0_0_25px_rgba(255,143,107,0.25)] transition hover:scale-[1.015] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : selectedBan ? "Update Ban" : "Save Ban"}
          </button>
        </div>
      </section>
    </section>
  );
}

function Field({
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  disabled = false,
}) {
  return (
    <label className="block">
      <span className="mb-2 block font-black uppercase tracking-[0.14em] text-[#ffb56f]">
        {label}
      </span>

      <input
        type={type}
        min={type === "number" ? "0" : undefined}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className="min-h-[54px] w-full rounded-2xl border border-white/10 bg-white/[0.04] px-5 font-bold text-white outline-none transition placeholder:text-white/25 focus:border-[#ffd978]/60 focus:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-45"
      />
    </label>
  );
}
